import { IconButton, List, Popover, Divider, Tooltip } from '@vapor/material'
import { Button } from '@mui/material'
import { FC, MouseEvent, useMemo, useState, useCallback, useEffect } from 'react'
import { ContextTenant, ContextProperty, ContextTenantUnusedPlants } from '../../hooks/context/context.types'
import { styled } from '@mui/material/styles'
import Apartment from '@mui/icons-material/Apartment'
import SupportAgent from '@mui/icons-material/SupportAgent'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ListTenant } from './list-tenant'
import { ListBackoffice } from './list-backoffice'
import { ListProperty } from './list-property'
import { AddProperty } from './add-property'

export type PropertySelectProps = {
  tenants: ContextTenant[]
  properties: ContextProperty[]
  value: { tenantId: string; propertyId: string | null } | null
  tenantUnusedPlants: ContextTenantUnusedPlants[]
  onChange: (value: { tenantId: string; propertyId: string | null }) => void
  onOnboarding: (onboardingTenantId: string) => void
  currentTenant?: ContextTenant
  currentPropertyId?: string
}

const PropertyButton = styled(Button)(({ theme }) => ({
  background: 'none !important',
  color: `${theme.palette.primary.contrastText} !important`,
  justifyContent: 'left !important',
  fontWeight: 'normal !important',
  ':hover': {
    background: 'hsl(199, 100%, 20%) !important',
    minHeight: '48px !important',
    height: '48px !important',
  },
}))

const PropertySelect: FC<PropertySelectProps> = (props) => {
  const [value, setValue] = useState(props.value)
  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null)
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    setValue(props.value)
  }, [props.value])

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const onPropertyClickHandler = useCallback(
    (newValue: { tenantId: string; propertyId: string | null }) => {
      setValue(newValue)
      setAnchorEl(null)
      if (props.onChange) {
        props.onChange(newValue)
      }
    },
    [props.onChange],
  )

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const property = useMemo(() => {
    return props.properties.find((p) => p.id === value?.propertyId)
  }, [value, props.properties])

  const tenant = useMemo(() => {
    return props.tenants.find((t) => t.id === value?.tenantId)
  }, [value, props.tenants])

  const onBackofficeClickHandler = (tenantId: string) => {
    if (props.onChange) {
      props.onChange({ tenantId: tenantId, propertyId: null })
    }
    navigate('/internal-support', { replace: true })
    setAnchorEl(null)
  }

  const areInBackofficeTenant = useMemo(() => props.currentTenant?.backoffice ?? false, [props.currentTenant])

  return (
    <>
      <PropertyButton
        sx={{ display: { xs: 'none', xl: 'inline-flex' } }}
        aria-describedby={id}
        startIcon={property?.name || !tenant?.backoffice ? <Apartment /> : <SupportAgent />}
        onClick={handleClick}
      >
        {property?.name || tenant?.description}
      </PropertyButton>
      <IconButton sx={{ display: { xs: 'flex', xl: 'none' } }} aria-describedby={id} onClick={handleClick}>
        <Tooltip title={property?.name || tenant?.description}>{property?.name || !tenant?.backoffice ? <Apartment /> : <SupportAgent />}</Tooltip>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List disablePadding sx={{ width: '220px' }}>
          {[...props.tenants]
            .sort((a, b) => (a.backoffice ? 1 : 0) - (b.backoffice ? 1 : 0))
            .map((tenant, index) => {
              const subHeader = <ListTenant key={`tenant-${index}`} name={tenant.description} isSelected={tenant.id === props.currentTenant?.id} />

              const properties =
                props.properties
                  .filter((p) => p.tenantId === tenant.id)
                  .map((p) => {
                    const isSelected = props.currentPropertyId && p.id === props.currentPropertyId ? true : false
                    return <ListProperty key={`${index}-${p.id}`} name={p.name} isSelected={isSelected} onClick={() => onPropertyClickHandler({ tenantId: tenant.id, propertyId: p.id })} />
                  }) ?? []

              const unusedPlants = props.tenantUnusedPlants.find((i) => i.tenantId === tenant.id)?.unusedPlants ?? []

              const addProperty = (
                <AddProperty key={`${index}-new-property`} onClick={() => props.onOnboarding(tenant.id)} totalPlants={properties.length + unusedPlants.length} unusedPlants={unusedPlants.length} />
              )

              const backofficeOption = tenant.backoffice ? <ListBackoffice key={`backoffice-${index}`} onClick={() => onBackofficeClickHandler(tenant.id)} /> : undefined

              return [
                <Divider light key={`divider-1-${index}`} />,
                subHeader,
                ...properties,
                !areInBackofficeTenant ? backofficeOption : undefined,
                !backofficeOption ? [<Divider light key={`divider-2-${index}`} />, addProperty] : undefined,
              ]
              // }
            })}
        </List>
      </Popover>
    </>
  )
}

export default PropertySelect
